import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { purchasingService } from "../services/dataService";
import { getStocks } from "../services/api";
import { PurchaseLineUpsert, PurchaseOrderDetail, PurchaseOrderUpsert, Stock, StockPriceHistory, Supplier } from "../types";
import { formatCurrency } from "../utils/formatters";
import { addDaysLocal, todayLocal } from "../utils/dates";
import { usePermissions } from "../context/PermissionContext";
import { useSettings } from "../context/SettingsContext";
import { poErr, poFmtDate, poFmtQty, poInput, poLayerBlocked, poNum, poParse } from "../utils/purchasing";
import ConfirmModal from "./ConfirmModal";
import SupplierModal from "./SupplierModal";
import { SupplierPickerField } from "./SupplierPicker";
import { StockPickerModal } from "./StockPicker";
import "../pages/PurchaseOrders.css";

/**
 * Satın alma siparişi modalı — yeni sipariş ve taslak düzenleme. Sayfaya gidilmez; liste arkada kalır.
 * Üç sabit bölge: başlık · kaydırılan belge gövdesi (sipariş bilgisi, kalemler, not) · toplamlar + eylemler.
 * Tedarikçi ve stok düz <select> değildir: SupplierPicker / StockPicker (fp-* seçici ailesi).
 */
export interface PurchaseOrderModalProps {
  /** Düzenlenen taslak; null = yeni sipariş. */
  order: PurchaseOrderDetail | null;
  initialSupplierId?: string;
  /** Tüm tedarikçiler (pasifler dahil); seçici yalnız aktifleri listeler. */
  suppliers: Supplier[];
  onSuppliersChanged: () => Promise<Supplier[]>;
  escDisabled?: boolean;
  /** Kirli form koruması modalde çözülür, sonra çağrılır. */
  onClose: () => void;
  onSaved: (r: { purchaseOrderId: string; number?: string; ordered: boolean; orderError?: string }) => void;
}

interface DraftLine { uid: string; stockId: string; stockCode: string; stockName: string; unit: string; description: string; quantity: string; unitPrice: string; vatRate: number }
type PhState = StockPriceHistory[] | "loading" | "error";

const CURRENCIES = ["TRY", "USD", "EUR", "GBP"];
let seq = 0;
let stockCache: Stock[] | null = null; // modal yeniden açıldığında liste anında gelsin; her açılışta tazelenir

const I = {
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>,
  send: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2 11 13M22 2l-7 20-4-9-9-4z" /></svg>,
};

/** Sunucu miktarı 3, birim fiyatı 4 basamağa yuvarlar; toplamlar da aynı değerden hesaplanır. */
const qtyOf = (l: DraftLine) => Math.round(poNum(l.quantity) * 1000) / 1000;
const priceOf = (l: DraftLine) => Math.round(poNum(l.unitPrice) * 10000) / 10000;
const priceBad = (l: DraftLine) => l.unitPrice.trim() !== "" && isNaN(poParse(l.unitPrice));

const Field: React.FC<{ label: string; hint?: string; span?: 2 | 3; as?: "label" | "div"; children: React.ReactNode }> = ({ label, hint, span, as = "label", children }) => {
  const Tag = as as any;
  return <Tag className={`po-field ${span ? `span${span}` : ""}`}><span>{label}{hint && <em>{hint}</em>}</span>{children}</Tag>;
};

const PurchaseOrderModal: React.FC<PurchaseOrderModalProps> = ({ order, initialSupplierId, suppliers, onSuppliersChanged, escDisabled, onClose, onSaved }) => {
  const { has, hasModule } = usePermissions();
  const { settings } = useSettings();
  const vatDefault = settings?.defaultVatRate ?? 20;
  const canSend = has("purchasing:orders:update");
  const showLevels = hasModule("stock");

  // ---- Form durumu (ilk değerler: taslak ya da boş sipariş) ----
  const presetSupplier = !order && initialSupplierId ? suppliers.find((s) => s.supplierId === initialSupplierId) : undefined;
  const [supplierId, setSupplierId] = useState(order?.supplierId ?? presetSupplier?.supplierId ?? "");
  const [orderDate, setOrderDate] = useState(order ? order.orderDate.slice(0, 10) : todayLocal());
  const [expectedDate, setExpectedDate] = useState(order?.expectedDate ? order.expectedDate.slice(0, 10) : "");
  const [currency, setCurrency] = useState(order?.currency ?? presetSupplier?.currency ?? "TRY");
  const [currencyTouched, setCurrencyTouched] = useState(false);
  const [note, setNote] = useState(order?.note ?? "");
  const [lines, setLines] = useState<DraftLine[]>(() => (order?.lines ?? []).map((l) => ({
    uid: `l${++seq}`, stockId: l.stockId, stockCode: l.stockCode || "", stockName: l.stockName, unit: l.unit,
    description: l.description || "", quantity: poInput(l.quantity), unitPrice: poInput(l.unitPrice), vatRate: l.vatRate,
  })));

  const [error, setError] = useState("");
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState<null | "draft" | "send">(null);
  const [pickerOpen, setPickerOpen] = useState<false | "multi" | string>(false); // "multi" ya da stoğu değişecek satırın uid'i
  const [ph, setPh] = useState<Record<string, PhState>>({}); // stockId → fiyat geçmişi
  const [phOpen, setPhOpen] = useState<Set<string>>(new Set()); // açık satır uid'leri
  const [guardOpen, setGuardOpen] = useState(false);
  const [zeroOpen, setZeroOpen] = useState(false);
  const [supplierModalOpen, setSupplierModalOpen] = useState(false);
  const [removed, setRemoved] = useState<{ line: DraftLine; index: number } | null>(null);
  const [focusUid, setFocusUid] = useState<string | null>(null);

  const [stocks, setStocks] = useState<Stock[]>(stockCache ?? []);
  const [stocksLoading, setStocksLoading] = useState(false);
  const [stocksError, setStocksError] = useState("");

  const overlayRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const addBtnRef = useRef<HTMLButtonElement>(null);
  const qtyRefs = useRef(new Map<string, HTMLInputElement>());
  const priceRefs = useRef(new Map<string, HTMLInputElement>());
  const createdRef = useRef<{ id: string; number?: string } | null>(null);
  const initialJson = useRef("");
  const seedCurrency = useRef(currency);
  const removedTimer = useRef<number | undefined>(undefined);

  // Açan öğe ilk render'da (tedarikçi alanı autoFocus ile odağı almadan) yakalanır; kapanışta odak ona döner
  const [opener] = useState(() => document.activeElement as HTMLElement | null);
  useEffect(() => () => { if (opener && opener.isConnected) opener.focus?.({ preventScroll: true }); }, [opener]);

  const supplier = suppliers.find((s) => s.supplierId === supplierId) ?? null;
  const stockOf = useCallback((id: string) => stocks.find((s) => s.id === id), [stocks]);

  const snapshot = () => JSON.stringify({ supplierId, orderDate, expectedDate, currency, note, lines: lines.map(({ uid, ...r }) => r) });
  const dirty = initialJson.current !== "" && snapshot() !== initialJson.current;

  // ---- Stoklar ----
  const loadStocks = useCallback(async () => {
    setStocksLoading(true); setStocksError("");
    try { const r = await getStocks(); stockCache = r.data as Stock[]; setStocks(stockCache); }
    catch { setStocksError("Stok listesi alınamadı."); }
    finally { setStocksLoading(false); }
  }, []);

  useEffect(() => {
    initialJson.current = snapshot();
    loadStocks();
    const t = window.setTimeout(() => {
      if (order && lines[0]) qtyRefs.current.get(lines[0].uid)?.focus();
      else if (supplierId) addBtnRef.current?.focus();
      // StrictMode geliştirmede geri-odak temizliğini bir kez erken çalıştırır; odak modal dışına kaçtıysa tedarikçi alanına döner
      else if (!overlayRef.current?.contains(document.activeElement)) overlayRef.current?.querySelector<HTMLElement>(".fp-control")?.focus();
    }, 40);
    return () => { window.clearTimeout(t); window.clearTimeout(removedTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Kaydedilmemiş değişiklik varken sekme kapatma/yenileme uyarısı
  useEffect(() => {
    if (!dirty) return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  // Yeni eklenen satırın miktarına odaklan (satır çizildikten sonra)
  useEffect(() => {
    if (!focusUid) return;
    const el = qtyRefs.current.get(focusUid);
    if (el) { el.focus(); el.select(); setFocusUid(null); }
  }, [focusUid, lines]);

  const vatOptions = useMemo(() => {
    const p = (settings?.vatRates || "").split(",").map((x) => parseFloat(x.trim().replace(",", "."))).filter((n) => !isNaN(n));
    const base = p.length ? p : [0, 1, 10, 20];
    return Array.from(new Set([...base, vatDefault, ...lines.map((l) => l.vatRate)])).sort((a, b) => a - b);
  }, [settings?.vatRates, vatDefault, lines]);

  const totals = useMemo(() => {
    let net = 0, vat = 0;
    for (const l of lines) { const n = qtyOf(l) * priceOf(l); net += n; vat += (n * l.vatRate) / 100; }
    return { net, vat, gross: net + vat };
  }, [lines]);

  const upd = (uid: string, patch: Partial<DraftLine>) => { setLines((p) => p.map((l) => (l.uid === uid ? { ...l, ...patch } : l))); setError(""); };
  const prefill = (s: Stock) => (s.unitPrice && s.unitPrice > 0 && (s.currency || "TRY") === currency ? poInput(s.unitPrice) : "");
  const toLine = (s: Stock): DraftLine => ({ uid: `l${++seq}`, stockId: s.id, stockCode: s.productCode || "", stockName: s.productName, unit: s.unit, description: "", quantity: "", unitPrice: prefill(s), vatRate: vatDefault });

  const pickSupplier = (s: Supplier | null) => {
    setSupplierId(s?.supplierId ?? ""); setError("");
    if (s && !order && !currencyTouched) { setCurrency(s.currency); seedCurrency.current = s.currency; }
  };
  const currencyChangedWithPrices = currencyTouched && currency !== seedCurrency.current && lines.some((l) => poNum(l.unitPrice) > 0);

  const removeLine = (index: number) => {
    const line = lines[index];
    setLines((p) => p.filter((_, k) => k !== index));
    setRemoved({ line, index });
    window.clearTimeout(removedTimer.current);
    removedTimer.current = window.setTimeout(() => setRemoved(null), 5000);
  };
  const undoRemove = () => {
    if (!removed) return;
    setLines((p) => { const n = [...p]; n.splice(Math.min(removed.index, n.length), 0, removed.line); return n; });
    setRemoved(null);
  };

  const togglePh = (l: DraftLine) => {
    setPhOpen((p) => { const n = new Set(p); if (n.has(l.uid)) n.delete(l.uid); else n.add(l.uid); return n; });
    if (ph[l.stockId] === undefined || ph[l.stockId] === "error") loadPh(l.stockId);
  };
  const loadPh = async (stockId: string) => {
    setPh((m) => ({ ...m, [stockId]: "loading" }));
    try { const r = await purchasingService.priceHistory(stockId); setPh((m) => ({ ...m, [stockId]: r })); }
    catch { setPh((m) => ({ ...m, [stockId]: "error" })); }
  };

  // ---- Satır içi klavye: Enter ile Miktar → Birim fiyat → sonraki satır; ↑↓ aynı sütunda ----
  const lineKey = (e: React.KeyboardEvent<HTMLInputElement>, index: number, col: "qty" | "price") => {
    const refs = col === "qty" ? qtyRefs.current : priceRefs.current;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      const next = lines[index + (e.key === "ArrowDown" ? 1 : -1)];
      if (next) { e.preventDefault(); refs.get(next.uid)?.focus(); }
      return;
    }
    if (e.key !== "Enter" || e.ctrlKey || e.metaKey) return;
    e.preventDefault();
    if (col === "qty") { priceRefs.current.get(lines[index].uid)?.focus(); return; }
    const next = lines[index + 1];
    if (next) qtyRefs.current.get(next.uid)?.focus();
    else setPickerOpen("multi");
  };

  // ---- Doğrulama ve kayıt ----
  const showError = (msg: string, focus?: () => void) => {
    setError(msg);
    if (focus) window.setTimeout(focus, 0); else bodyRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };
  const focusInput = (el?: HTMLInputElement | null) => () => { el?.focus(); el?.scrollIntoView({ block: "center" }); };

  const validate = (): boolean => {
    setTried(true);
    if (!supplierId) { showError("Tedarikçi seçin."); return false; }
    if (supplier && !supplier.isActive) { showError("Seçili tedarikçi pasif; aktif bir tedarikçi seçin."); return false; }
    if (!orderDate) { showError("Sipariş tarihi girin."); return false; }
    if (orderDate > todayLocal()) { showError("Sipariş tarihi gelecekte olamaz."); return false; }
    if (expectedDate && expectedDate < orderDate) { showError("Beklenen teslim, sipariş tarihinden önce olamaz."); return false; }
    if (lines.length === 0) { showError("En az bir kalem ekleyin."); return false; }
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i];
      const q = poParse(l.quantity);
      const qtyMsg = l.quantity.trim() === "" || q === 0 ? "miktar sıfırdan büyük olmalı" : isNaN(q) ? "miktar geçerli bir sayı değil (örn. 1250 ya da 1.250,5)" : qtyOf(l) <= 0 ? "miktar en az 0,001 olmalı" : "";
      if (qtyMsg) { showError(`Satır ${i + 1}: ${qtyMsg}.`, focusInput(qtyRefs.current.get(l.uid))); return false; }
      if (priceBad(l)) { showError(`Satır ${i + 1}: birim fiyat geçerli bir sayı değil (örn. 12,50).`, focusInput(priceRefs.current.get(l.uid))); return false; }
    }
    return true;
  };

  const dto = (): PurchaseOrderUpsert => ({
    supplierId, orderDate, expectedDate: expectedDate || null, currency, note: note.trim() || null,
    lines: lines.map<PurchaseLineUpsert>((l) => ({ stockId: l.stockId, description: l.description.trim() || null, quantity: qtyOf(l), unitPrice: priceOf(l), vatRate: l.vatRate })),
  });
  const zeroCount = lines.filter((l) => priceOf(l) === 0).length;

  const submit = async (send: boolean, zeroOk = false) => {
    if (busy) return;
    if (!validate()) return;
    if (send && zeroCount > 0 && !zeroOk) { setZeroOpen(true); return; }
    setBusy(send ? "send" : "draft"); setError("");
    let id = order?.purchaseOrderId ?? createdRef.current?.id;
    let number = order?.number ?? createdRef.current?.number;
    try {
      if (id) await purchasingService.updateOrder(id, dto());
      else { const r = await purchasingService.createOrder(dto()); id = r.purchaseOrderId; number = r.number; createdRef.current = { id, number }; } // yeniden denemede ikinci sipariş açılmasın
    } catch (e) { setBusy(null); showError(poErr(e, "Sipariş kaydedilemedi.")); return; }
    if (send) {
      try { await purchasingService.markOrdered(id!); }
      catch (e) { onSaved({ purchaseOrderId: id!, number, ordered: false, orderError: poErr(e, "Sipariş iletilemedi.") }); return; }
    }
    onSaved({ purchaseOrderId: id!, number, ordered: send });
  };

  const finish = () => (createdRef.current ? onSaved({ purchaseOrderId: createdRef.current.id, number: createdRef.current.number, ordered: false }) : onClose());
  const requestClose = () => { if (busy) return; if (dirty) setGuardOpen(true); else finish(); };

  // ---- Modal kısayolları: Esc kapat · Ctrl+Enter / Ctrl+S taslak kaydet · F2 stok ekle ----
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (escDisabled || guardOpen || zeroOpen || supplierModalOpen || poLayerBlocked(overlayRef.current)) return;
      // stopPropagation şart: aynı tuş basımında yeniden kaydolan dinleyiciler (arkadaki çekmece, açılan kirli-form onayı) Esc'i ikinci kez almasın
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); requestClose(); }
      else if ((e.ctrlKey || e.metaKey) && (e.key === "Enter" || e.key.toLowerCase() === "s")) { e.preventDefault(); submit(false); }
      else if (e.key === "F2") { e.preventDefault(); setPickerOpen("multi"); }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  });

  const pickerLine = pickerOpen && pickerOpen !== "multi" ? lines.find((l) => l.uid === pickerOpen) : undefined;

  return (
    <div className="po-overlay center" ref={overlayRef} onMouseDown={(e) => { if (e.target === e.currentTarget) requestClose(); }}>
      <form className="po-modal xl" role="dialog" aria-modal="true" aria-labelledby="po-om-title" onSubmit={(e) => e.preventDefault()}>
        <div className="po-modal-head">
          <div>
            <span className="po-kicker">Satın alma</span>
            <h3 id="po-om-title">{order ? `Taslağı düzenle · ${order.number}` : "Yeni sipariş"}</h3>
            <p className="po-form-hint">Taslak olarak kaydedin ya da doğrudan tedarikçiye iletin.</p>
          </div>
          <div className="po-modal-head-side">
            {dirty && <span className="po-dirty">Kaydedilmemiş değişiklik</span>}
            <button type="button" className="po-ib big" aria-label="Kapat" onClick={requestClose}>{I.close}</button>
          </div>
        </div>

        <div className="po-modal-body" ref={bodyRef}>
          {error && <div className="po-note err" role="alert">{error}</div>}

          {/* A — Sipariş */}
          <div className="po-form-sec"><h4>Sipariş</h4>
            <div className="po-grid4">
              <Field label="Tedarikçi" hint="zorunlu" span={2} as="div">
                <SupplierPickerField value={supplierId} suppliers={suppliers} invalid={tried && !supplierId} autoFocus={!order && !supplierId}
                  onChange={pickSupplier} onCreate={has("purchasing:suppliers:create") ? () => setSupplierModalOpen(true) : undefined} />
                {supplier && <span className="po-fieldnote">{[supplier.contactPerson, supplier.phone, supplier.email].filter(Boolean).join(" · ") || "İletişim bilgisi yok"} · Vade {supplier.paymentTermDays} gün</span>}
                {supplier && !supplier.isActive && <span className="po-fielderr">Bu tedarikçi pasif; kaydetmek için aktif bir tedarikçi seçin.</span>}
              </Field>
              <Field label="Sipariş tarihi"><input type="date" value={orderDate} max={todayLocal()} onChange={(e) => { setOrderDate(e.target.value); setError(""); }} /></Field>
              <Field label="Beklenen teslim" hint="isteğe bağlı" as="div">
                <input type="date" aria-label="Beklenen teslim" value={expectedDate} min={orderDate} onChange={(e) => { setExpectedDate(e.target.value); setError(""); }} />
                <div className="po-quick">{[7, 15, 30].map((n) => <button type="button" key={n} onClick={() => setExpectedDate(addDaysLocal(orderDate || todayLocal(), n))}>+{n} gün</button>)}</div>
              </Field>
              <Field label="Para birimi" span={2} as="div">
                <div className="po-seg" role="radiogroup" aria-label="Para birimi">
                  {CURRENCIES.map((c) => <button type="button" key={c} role="radio" aria-checked={currency === c} className={currency === c ? "on" : ""} onClick={() => { setCurrency(c); setCurrencyTouched(true); }}>{c}</button>)}
                </div>
                {currencyChangedWithPrices && <span className="po-fieldnote warn">Para birimi değişti; birim fiyatlar çevrilmedi, kontrol edin.</span>}
              </Field>
            </div>
          </div>

          {/* B — Kalemler */}
          <div className="po-form-sec">
            <h4>Kalemler <em>{lines.length}</em>
              <button ref={addBtnRef} type="button" className="po-btn small" aria-haspopup="dialog" onClick={() => setPickerOpen("multi")}>{I.plus} Stok ekle <kbd className="po-kbd">F2</kbd></button>
            </h4>
            {lines.length === 0 ? (
              <div className="po-blank small"><b>Henüz kalem yok</b>Sipariş edilecek stokları ekleyin.
                <button type="button" className="po-btn" onClick={() => setPickerOpen("multi")}>{I.plus} Stok ekle</button></div>
            ) : (
              <div className="po-lines">
                <div className="po-lines-head"><span>#</span><span>Stok</span><span className="num">Miktar</span><span className="num">Birim fiyat</span><span>KDV</span><span className="num">Tutar</span><span /></div>
                {lines.map((l, i) => {
                  const s = stockOf(l.stockId);
                  const n = qtyOf(l) * priceOf(l);
                  const low = !!s && s.minimumStock != null && s.currentStock <= s.minimumStock;
                  const last = s?.unitPrice && s.unitPrice > 0 && (s.currency || "TRY") === currency ? s.unitPrice : 0;
                  const price = priceOf(l);
                  const pct = last > 0 && price > 0 ? Math.round(((price - last) / last) * 100) : 0;
                  const hist = ph[l.stockId];
                  return (
                    <div className="po-line" key={l.uid}>
                      <div className="c-no">{i + 1}</div>
                      <div className="c-stock">
                        <button type="button" className="po-stockbtn" title="Stoğu değiştir" onClick={() => setPickerOpen(l.uid)}><b>{l.stockName}</b>{l.stockCode && <span className="po-codepill">{l.stockCode}</span>}</button>
                        <input className="po-ghost" value={l.description} maxLength={200} placeholder="Açıklama ekle" aria-label={`${l.stockName} açıklama`} onChange={(e) => upd(l.uid, { description: e.target.value })} />
                        <div className="po-hintline">
                          {showLevels && s && <>Mevcut <b className={low ? "low" : ""}>{poFmtQty(s.currentStock)} {s.unit}</b> · </>}
                          {s?.unitPrice ? <>Son alış <b>{formatCurrency(s.unitPrice, s.currency || "TRY")}</b>{(s.currency || "TRY") !== currency && <span className="po-badge warn">farklı para birimi</span>} · </> : null}
                          <button type="button" className="po-link" tabIndex={-1} aria-expanded={phOpen.has(l.uid)} onClick={() => togglePh(l)}>Fiyat geçmişi</button>
                        </div>
                      </div>
                      <div className="c-qty">
                        <div className="po-money">
                          <input ref={(el) => { if (el) qtyRefs.current.set(l.uid, el); else qtyRefs.current.delete(l.uid); }} inputMode="decimal" aria-label={`${l.stockName} miktar`}
                            className={tried && !(qtyOf(l) > 0) ? "is-invalid" : ""} value={l.quantity} placeholder="0"
                            onFocus={(e) => e.currentTarget.select()} onKeyDown={(e) => lineKey(e, i, "qty")} onChange={(e) => upd(l.uid, { quantity: e.target.value })} />
                          <i>{l.unit}</i>
                        </div>
                      </div>
                      <div className="c-price">
                        <div className="po-money">
                          <input ref={(el) => { if (el) priceRefs.current.set(l.uid, el); else priceRefs.current.delete(l.uid); }} inputMode="decimal" aria-label={`${l.stockName} birim fiyat`}
                            className={tried && priceBad(l) ? "is-invalid" : ""} value={l.unitPrice} placeholder="0,00"
                            onFocus={(e) => e.currentTarget.select()} onKeyDown={(e) => lineKey(e, i, "price")} onChange={(e) => upd(l.uid, { unitPrice: e.target.value })} />
                          <i>{currency}</i>
                        </div>
                        {Math.abs(pct) >= 10 && <span className={`po-delta ${pct > 0 ? "up" : "down"}`} title="Son alış fiyatına göre">{pct > 0 ? "▲" : "▼"} %{Math.abs(pct)}</span>}
                      </div>
                      <div className="c-vat">
                        <select className="po-select" aria-label="KDV oranı" value={l.vatRate} onChange={(e) => upd(l.uid, { vatRate: parseFloat(e.target.value) })}>
                          {vatOptions.map((r) => <option key={r} value={r}>%{r}</option>)}
                        </select>
                      </div>
                      <div className="c-amt">{n > 0 ? formatCurrency(n, currency) : "—"}</div>
                      <div className="c-act"><button type="button" className="po-ib danger" tabIndex={-1} title="Satırı kaldır" aria-label={`${l.stockName} satırını kaldır`} onClick={() => removeLine(i)}>{I.close}</button></div>
                      {phOpen.has(l.uid) && (
                        <div className="po-ph">
                          <h5>Son alışlar</h5>
                          {hist === undefined || hist === "loading" ? "Yükleniyor"
                            : hist === "error" ? <>Fiyat geçmişi alınamadı. <button type="button" className="po-link" onClick={() => loadPh(l.stockId)}>Yeniden dene</button></>
                              : hist.length === 0 ? "Bu stok için mal kabul geçmişi yok."
                                : hist.slice(0, 8).map((h, k) => {
                                  const mine = !!supplier && h.supplierName === supplier.name;
                                  return (
                                    <div className={`po-ph-row ${mine ? "mine" : ""}`} key={k}>
                                      <span>{poFmtDate(h.date)}</span>
                                      <span>{h.supplierName}{mine && <span className="po-badge info">bu tedarikçi</span>}</span>
                                      <span>{h.orderNumber}</span>
                                      <span className="num">{poFmtQty(h.quantity)}</span>
                                      <b className="num">{formatCurrency(h.unitPrice, h.currency)}</b>
                                      <button type="button" className="po-link" disabled={h.currency !== currency} title={h.currency !== currency ? "Para birimi farklı" : undefined} onClick={() => upd(l.uid, { unitPrice: poInput(h.unitPrice) })}>Bu fiyatı kullan</button>
                                    </div>
                                  );
                                })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {removed && <div className="po-undo" role="status">{removed.line.stockName} kaldırıldı · <button type="button" className="po-link" onClick={undoRemove}>Geri al</button></div>}
          </div>

          {/* C — Not */}
          <div className="po-form-sec"><h4>Not</h4>
            <label className="po-field"><textarea rows={2} maxLength={500} value={note} aria-label="Not" placeholder="Tedarikçiye not, teslimat koşulu…" onChange={(e) => setNote(e.target.value)} /></label>
          </div>
        </div>

        <div className="po-modal-foot between">
          <div className="po-sum">
            <div><span>Ara toplam</span><b>{formatCurrency(totals.net, currency)}</b></div>
            <div><span>KDV</span><b>{formatCurrency(totals.vat, currency)}</b></div>
            <div className="grand"><span>Genel toplam</span><b>{formatCurrency(totals.gross, currency)}</b></div>
          </div>
          <div className="po-foot-btns">
            <button type="button" className="po-btn" onClick={requestClose} disabled={!!busy}>Vazgeç</button>
            <button type="button" className={`po-btn ${canSend ? "" : "primary"}`} onClick={() => submit(false)} disabled={!!busy}>{busy === "draft" ? "Kaydediliyor" : order ? "Taslağı kaydet" : "Taslak kaydet"}</button>
            {canSend && <button type="button" className="po-btn primary" onClick={() => submit(true)} disabled={!!busy}>{I.send} {busy === "send" ? "İletiliyor" : "Kaydet ve ilet"}</button>}
          </div>
        </div>
      </form>

      {/* Seçiciler ve üst katmanlar */}
      <StockPickerModal
        open={!!pickerOpen}
        mode={pickerOpen === "multi" ? "multi" : "single"}
        onClose={() => setPickerOpen(false)}
        stocks={stocks} loading={stocksLoading} error={stocksError} onReload={loadStocks}
        onDocument={lines.map((l) => l.stockId)}
        supplierId={supplierId || undefined} supplierName={supplier?.name}
        showLevels={showLevels}
        onApply={(picked) => {
          if (pickerOpen === "multi") {
            const added = picked.map(toLine);
            setLines((p) => [...p, ...added]); setError("");
            if (added[0]) setFocusUid(added[0].uid);
          } else if (pickerLine && picked[0]) {
            const s = picked[0];
            upd(pickerLine.uid, { stockId: s.id, stockCode: s.productCode || "", stockName: s.productName, unit: s.unit, unitPrice: prefill(s) || pickerLine.unitPrice });
            setPhOpen((p) => { if (!p.has(pickerLine.uid)) return p; const n = new Set(p); n.delete(pickerLine.uid); return n; }); // eski stoğun geçmişi açık kalmasın
            setFocusUid(pickerLine.uid);
          }
        }}
      />

      {supplierModalOpen && (
        <SupplierModal onClose={() => setSupplierModalOpen(false)} onSaved={async (newId) => {
          try {
            const list = await onSuppliersChanged();
            const s = list.find((x) => x.supplierId === newId);
            if (s) { setSupplierId(s.supplierId); setError(""); if (!order && !currencyTouched) { setCurrency(s.currency); seedCurrency.current = s.currency; } }
          } catch { showError("Tedarikçi oluşturuldu ancak liste yenilenemedi; tedarikçi seçicisini yeniden açın."); }
        }} />
      )}

      <ConfirmModal isOpen={guardOpen} type="warning" title="Kaydedilmemiş değişiklikler" message="Bu siparişteki değişiklikler kaydedilmedi. Kapatırsanız kaybolur."
        confirmText="Kaydetmeden kapat" cancelText="Düzenlemeye dön" onConfirm={() => { setGuardOpen(false); finish(); }} onCancel={() => setGuardOpen(false)} />
      <ConfirmModal isOpen={zeroOpen} type="warning" title="Birim fiyat 0" message={`${zeroCount} kalemde birim fiyat 0. Yine de iletilsin mi?`}
        confirmText="İlet" cancelText="Vazgeç" onConfirm={() => { setZeroOpen(false); submit(true, true); }} onCancel={() => setZeroOpen(false)} />
    </div>
  );
};

export default PurchaseOrderModal;
